import { getCurrentUserId } from "@risalabs_frontend_org/auth-wrapper";
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import { closeModal } from "../../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../../redux/store/store";
import { NarAgentGridBulkToggleAction } from "../utils/narAgentGridBulkToggleActions";
import {
  createBulkTogglePayload,
  getSelectedNarAgentGridTableRows,
} from "../utils/narAgentGridBulkTogglePayload";
import { useNarAgentGridBulkToggle } from "./useNarAgentGridBulkToggle";

interface UseNarAgentGridBulkToggleConfirmParams {
  tableData: any[];
  clientId: string;
  action: NarAgentGridBulkToggleAction | undefined;
  onAuthGridRefetch: () => Promise<void>;
}

/**
 * Builds the bulk toggle payload, calls the API, shows result toasts, and
 * refetches auth grid data on success.
 */
export const useNarAgentGridBulkToggleConfirm = ({
  tableData,
  clientId,
  action,
  onAuthGridRefetch,
}: UseNarAgentGridBulkToggleConfirmParams) => {
  const dispatch = useDispatch<AppDispatch>();

  const { loading: bulkToggleLoading, bulkToggle } = useNarAgentGridBulkToggle({
    onError: (message) => {
      toast.error(message || ToastMessages.NAR_AGENT_GRID_BULK_TOGGLE_FAILURE);
    },
  });

  const handleBulkToggleModalConfirm = useCallback(
    async (remarks: string) => {
      if (!action || bulkToggleLoading) {
        return;
      }

      const payload = createBulkTogglePayload(
        action,
        getSelectedNarAgentGridTableRows(tableData),
        clientId,
        remarks,
        getCurrentUserId() ?? "",
      );

      const response = await bulkToggle(payload);
      if (!response) {
        return;
      }

      if (response.failed === 0) {
        toast.success(
          `${ToastMessages.NAR_AGENT_GRID_BULK_TOGGLE_SUCCESS} ${response.succeeded} row(s) updated.`,
        );
        dispatch(closeModal());
      } else if (response.succeeded > 0) {
        toast.error(
          `${ToastMessages.NAR_AGENT_GRID_BULK_TOGGLE_PARTIAL_FAILURE} ${response.succeeded} succeeded, ${response.failed} failed.`,
        );
        dispatch(closeModal());
      } else {
        toast.error(ToastMessages.NAR_AGENT_GRID_BULK_TOGGLE_FAILURE);
        return;
      }

      await onAuthGridRefetch();
    },
    [
      action,
      bulkToggleLoading,
      bulkToggle,
      clientId,
      dispatch,
      onAuthGridRefetch,
      tableData,
    ],
  );

  return {
    bulkToggleLoading,
    handleBulkToggleModalConfirm,
  };
};
